import type {
    CommerceCatalogueExperience,
    CommerceOperationOptions,
    CommerceProduct,
    CommerceProductCatalogue,
    CommerceProductCategory,
    CommerceProductIdentity,
    CommerceProductLookup,
    CommerceProductSummary,
    ResolvedCommerceProductDetailConfiguration,
    SearchCommerceProductsRequest,
    SearchCommerceProductsResponse,
} from "@teqtank/commerce-contracts";
import { ProductCatalogueRepository } from "./product-catalogue-repository.js";

export class CommerceProductCatalogueService implements CommerceProductCatalogue {
    public constructor(
        private readonly repository: ProductCatalogueRepository,
        private readonly productDetail: ResolvedCommerceProductDetailConfiguration,
    ) {}

    public search(
        request: SearchCommerceProductsRequest = {},
        options: CommerceOperationOptions = {},
    ): Promise<SearchCommerceProductsResponse> {
        return this.repository.search(request, options);
    }

    public get(
        identifier: string,
        identity: CommerceProductIdentity = this.productDetail.identity,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.repository.get(identifier, identity, options);
    }

    public getById(
        productId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.repository.getById(productId, options);
    }

    public getBySlug(
        slug: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.repository.getBySlug(slug, options);
    }

    public getBySku(sku: string, options: CommerceOperationOptions = {}): Promise<CommerceProduct> {
        return this.repository.getBySku(sku, options);
    }

    public getByIdInExperience(
        productId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.repository.getByIdInExperience(productId, experience, options);
    }

    public getByIdInCatalogue(
        catalogueId: string,
        productId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.repository.getByIdInCatalogue(catalogueId, productId, experience, options);
    }

    public getDetailUrl(product: CommerceProductSummary): string {
        const identifier = getProductIdentifier(product, this.productDetail.identity);
        const encodedIdentifier = encodeURIComponent(identifier);

        if (this.productDetail.routeTemplate.includes("{identifier}")) {
            return this.productDetail.routeTemplate.replaceAll("{identifier}", encodedIdentifier);
        }

        const separator = this.productDetail.routeTemplate.includes("?") ? "&" : "?";
        return `${this.productDetail.routeTemplate}${separator}${encodeURIComponent(this.productDetail.queryParameter)}=${encodedIdentifier}`;
    }

    public resolveDetailLookup(
        location: string | URL = globalThis.location?.href ?? "http://localhost/",
    ): CommerceProductLookup | null {
        const url =
            location instanceof URL
                ? location
                : new URL(location, globalThis.location?.origin ?? "http://localhost");
        const queryValue = url.searchParams.get(this.productDetail.queryParameter)?.trim();

        if (queryValue) {
            return { identifier: queryValue, identity: this.productDetail.identity };
        }

        const routeIdentifier = resolveRouteIdentifier(
            this.productDetail.routeTemplate,
            url.pathname,
        );

        return routeIdentifier === null
            ? null
            : { identifier: routeIdentifier, identity: this.productDetail.identity };
    }

    public getCategoryById(
        categoryId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProductCategory> {
        return this.repository.getCategoryById(categoryId, options);
    }

    public getCategoryByIdInCatalogue(
        catalogueId: string,
        categoryId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProductCategory> {
        return this.repository.getCategoryByIdInCatalogue(
            catalogueId,
            categoryId,
            experience,
            options,
        );
    }

    public getCategories(
        parentId?: string | null,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceProductCategory[]> {
        return this.repository.getCategories(parentId, options);
    }

    public getCategoriesInCatalogue(
        catalogueId: string,
        experience: CommerceCatalogueExperience,
        parentId?: string | null,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceProductCategory[]> {
        return this.repository.getCategoriesInCatalogue(catalogueId, experience, parentId, options);
    }

    public clearCache(): void {
        this.repository.clearCache();
    }
}

function getProductIdentifier(
    product: CommerceProductSummary,
    identity: CommerceProductIdentity,
): string {
    switch (identity) {
        case "id":
            return product.id;
        case "sku":
            return product.displaySku ?? product.id;
        case "slug":
        default:
            return product.slug;
    }
}

function resolveRouteIdentifier(routeTemplate: string, pathname: string): string | null {
    if (!routeTemplate.includes("{identifier}")) {
        return null;
    }

    const escaped = routeTemplate
        .split("{identifier}")
        .map((part) => part.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&"))
        .join("([^/]+)");
    const match = new RegExp(`^${escaped}/?$`, "u").exec(pathname);

    return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}
